import React from "react";
import styled from "styled-components";
import { object as obj, func } from "prop-types";

import { carerCardClick } from "data/segmentCustomEvents";
import CarerImage from "components/postcode_search/CarerImage";
import ProfileDataInfo from "components/postcode_search/ProfileDataInfo";

import {
  determineAdditionalArray,
  ensureLanguageIsFirst
} from "components/postcode_search/CarerCardHelper";
import {
  conditionExperienceColours,
  servicesColours
} from "components/postcode_search/colourConfig";
import { renderTaxon, renderTaxonomiesFromArray } from "utils";

const SharedFlexStyles = `
  display: flex;
  flex-direction: column;
`;

const StyledCarerCard = styled.div`
  ${SharedFlexStyles}
  align-items: center;
  flex-grow: 1;

  background: white;
  border-radius: 5px;
  padding: 1rem;
  width: 300px;
  max-width: 400px;
  position: relative;
  overflow: hidden;
  margin: 0 10px 20px 10px;
  cursor: pointer;
`;

const StyledCarerData = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const StyledExperience = styled.div`
  font-size: 19px;
  font-weight: 600;
  color: ${props => props.theme.colors.midGrey};
`;

const StyledCarerName = styled.div`
  font-size: 32px;
  color: ${props => props.theme.colors.darkBlueLow};
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 160px;
  white-space: nowrap;
  line-height: 40px;
`;

const StyledProfileData = styled.div`
  ${SharedFlexStyles}
  align-items: baseline;
  justify-content: space-around;
  text-align: left;
  width: 100%;
  font-size: 20px;
  overflow: hidden;
  position: relative;

  &:before {
    content: "";
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 0) 90%, white);
  }
`;

const CarerCard = ({ carer, taxonomies, onClick }) => {
  if (!carer || !taxonomies) {
    return null;
  }

  const { profile, user } = carer;

  const additionalData = determineAdditionalArray(profile.profile_data);

  const triggerEventAndAction = () => {
    carerCardClick();
    onClick();
  };

  return (
    <StyledCarerCard
      className="carer-card"
      data-testid="carer-card"
      onClick={triggerEventAndAction}
    >
      <StyledCarerData>
        <div>
          {user.firstname && (
            <StyledCarerName id="carerName">{user.firstname}</StyledCarerName>
          )}
          {profile.profile_data.care_experience_years && (
            <StyledExperience id="experience">{`${renderTaxon(
              taxonomies.care_experience_years,
              profile.profile_data.care_experience_years
            )}’ experience`}</StyledExperience>
          )}
        </div>
        <CarerImage carer={user} />
      </StyledCarerData>
      <StyledProfileData>
        {profile.profile_data.languages && (
          <ProfileDataInfo
            id="languages"
            label="SPEAKS"
            values={renderTaxonomiesFromArray(
              ensureLanguageIsFirst(
                profile.profile_data.languages.map(lang => lang.language),
                "eng"
              ),
              taxonomies.languages
            )}
          />
        )}
        {profile.profile_data.condition_experience && (
          <ProfileDataInfo
            id="conditionExp"
            label="EXPERIENCE WITH"
            values={renderTaxonomiesFromArray(
              profile.profile_data.condition_experience.slice(0, 3),
              taxonomies.condition,
              "condition"
            )}
            extraStyles={conditionExperienceColours}
          />
        )}
        {profile.profile_data.services && (
          <ProfileDataInfo
            id="conditionExp"
            label="CAN HELP WITH"
            values={renderTaxonomiesFromArray(
              profile.profile_data.services.slice(0, 3),
              taxonomies.services
            )}
            extraStyles={servicesColours}
          />
        )}
        {!!additionalData.length && (
          <ProfileDataInfo
            id="additional"
            label="ADDITIONAL"
            values={additionalData}
          />
        )}
      </StyledProfileData>
    </StyledCarerCard>
  );
};

CarerCard.propTypes = {
  carer: obj.isRequired,
  taxonomies: obj.isRequired,
  onClick: func.isRequired
};

export default CarerCard;
